import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import ReportActionsMenu from "./ReportActionsMenu";
import CustomPagination from "../../../components/customPagination";
import { formatDate, formatDateWithTimestamp } from "../../../utils/formatDate";
import { fetchUser } from "../../settings/dataHandlers/getUserHandler";
import DocumentTableStatusChip from "../../common/DocumentTableStatusChip";
import {useSelector} from "react-redux";

const CreatedByCell = ({ userId }) => {
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["auth-user", userId],
    queryFn: () => fetchUser(userId),
    enabled: Boolean(userId),
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (!userId) {
    return "-";
  }

  if (isLoading) {
    return "Loading...";
  }

  if (isError) {
    return "-";
  }

  return user?.name || user?.email || "-";
};

const formatVatNumbers = (vatNumbers) => {
  if (!Array.isArray(vatNumbers) || vatNumbers.length === 0) {
    return "-";
  }

  const labels = vatNumbers
    .map((vat) => (vat == null ? "" : String(vat).trim()))
    .filter(Boolean);

  return labels.length ? labels.join(", ") : "-";
};

const ReportTable = ({
  reports,
  loading,
  totalReports,
  onPageChange,
  onPageSizeChange,
}) => {
  const user = useSelector((state) => state.user);
  const isRootUser = user && user.user.is_root_user;
  const columns = [
    {
      field: "period",
      headerName: "Accounting Period",
      flex: 1,
      align: "left",
      renderCell: (params) => {
        const start = formatDate(params.row?.created_after) || "";
        const end = formatDate(params.row?.created_before) || "";
        const to = "to";
        return `${start} ${to} ${end}`;
      },
    },
    ...(reports.some((report) => report.type === "VAT_REPORT")
      ? [
          {
            field: "vat_numbers",
            headerName: "VAT No.",
            flex: 1,
            align: "left",
            renderCell: (params) => formatVatNumbers(params?.row?.vat_numbers),
          },
        ]
      : []),

    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      align: "left",
      id: "report-status",
      renderCell: (params) => (
        <DocumentTableStatusChip status={params.row.status?.toUpperCase()} />
      ),
    },
    {
      field: "created_by",
      headerName: "Created by",
      flex: 0.8,
      align: "left",
      renderCell: (params) => isRootUser? <CreatedByCell userId={params.row.created_by}/> : user?.user.name || user?.user?.email || "-",
    },
    {
      field: "created_at",
      headerName: "Creation Date",
      flex: 0.7,
      align: "left",
      renderCell: (params) => formatDateWithTimestamp(params.row.created_at),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const isProcessed = params.row.status?.toLowerCase() === "processed";
        return (
          <ReportActionsMenu
            filePath={params.row.file_path}
            disabled={!isProcessed}
          />
        );
      },
    },
  ];

  return (
    <DataGrid
      columns={columns}
      rows={reports}
      getRowId={(row) => row.id}
      sx={{ mt: "1rem" }}
      disableRowSelectionOnClick={true}
      loading={loading}
      paginationMode="server"
      rowCount={totalReports}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
      }}
      pageSizeOptions={[10, 25]}
      onPaginationModelChange={({ page, pageSize }) => {
        onPageChange(page);
        onPageSizeChange(pageSize);
      }}
      slots={{
        pagination: CustomPagination,
      }}
    />
  );
};

export default ReportTable;
